import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Ban } from "lucide-react";

function Section({ title, children }) {
  return (
    <section className="border-t border-line py-8 first:border-t-0">
      <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-cream mb-4">{title}</h2>
      <div className="text-ash leading-relaxed space-y-4 max-w-3xl text-sm md:text-base">{children}</div>
    </section>
  );
}

export function Terms() {
  return (
    <div className="min-h-screen bg-ink" data-testid="terms-page">
      <Header />
      <main className="max-w-[1000px] mx-auto px-5 md:px-10 pt-28 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">The fine print</p>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold tracking-[-0.03em] text-cream mb-3">Terms of Use</h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ash/60 mb-10">Last updated: June 2026</p>

        <div className="border border-gold/40 bg-gold/5 p-6 md:p-8 mb-10 flex gap-5 items-start" data-testid="terms-noai-highlight">
          <Ban size={40} className="text-gold shrink-0" />
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-cream mb-2">Our strict NO-AI policy</h2>
            <p className="text-ash leading-relaxed text-sm md:text-base">
              WriterHaus is a room for real people making real music. Every song, video, and piece of media you
              upload must be created by a human. AI-generated music, AI-generated vocals, AI-written lyrics passed
              off as your own, and AI-generated media are strictly prohibited. When you upload, you attest that your
              work is human-made. Violating this policy is grounds for immediate content takedown and account removal.
            </p>
          </div>
        </div>

        <Section title="1. Who we are">
          <p>WriterHaus is a private, curated home for indie songwriters, artists and producers to share a small
            body of their best work and connect with collaborators. By creating an account or using the platform,
            you agree to these Terms of Use.</p>
        </Section>

        <Section title="2. Eligibility & accounts">
          <p>You must be at least 18 years old to use WriterHaus. You are responsible for keeping your login secure
            and for all activity on your account. Provide accurate information and keep your HausCard truthful.</p>
        </Section>

        <Section title="3. The NO-AI attestation">
          <p>WriterHaus exists for human-made music. Each time you upload a song, an intro video, or other media,
            you explicitly attest that the work is created by humans and is not AI-generated. This attestation is
            required — you cannot publish media without it.</p>
          <p>We publish instantly and rely on the community. Members can report and flag content they believe
            violates the NO-AI policy, and our takedown queue moves quickly. We reserve the right to remove any
            content and terminate any account that breaches this policy, at our sole discretion.</p>
        </Section>

        <Section title="4. Your content">
          <p>You retain ownership of the songs, images, and videos you upload. You grant WriterHaus a limited license
            to host and display your content on the platform so other members can discover you. You are responsible
            for having the rights to everything you upload.</p>
        </Section>

        <Section title="5. Membership & access">
          <p>Creating a HausCard is free. Browsing the Haus, hearing songs, and requesting intros require a paid
            membership ($8/month or $59/year, which auto-renews annually). Access is granted and revoked based on your active subscription status.</p>
        </Section>

        <Section title="6. Conduct">
          <p>Be decent. No harassment, spam, impersonation, or unsolicited commercial messages. Intro requests and
            direct messages are for genuine collaboration. Abuse of the messaging system may result in removal.</p>
        </Section>

        <Section title="7. Termination">
          <p>You may delete your account at any time. We may suspend or terminate accounts that violate these terms,
            especially the NO-AI policy, without prior notice.</p>
        </Section>

        <Section title="8. Contact">
          <p>Questions about these terms? Reach us at info@underground-mix.com.</p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

export function Privacy() {
  return (
    <div className="min-h-screen bg-ink" data-testid="privacy-page">
      <Header />
      <main className="max-w-[1000px] mx-auto px-5 md:px-10 pt-28 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">The fine print</p>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold tracking-[-0.03em] text-cream mb-3">Privacy Policy</h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ash/60 mb-10">Last updated: June 2026</p>

        <Section title="1. What we collect">
          <p>We collect the information you give us to build your HausCard: your name, email, bio, location, role
            and genre tags, social links, and the songs, images, and video you upload. We also store account and
            usage data needed to run the platform (login sessions, messages you send, HausCards you save).</p>
        </Section>

        <Section title="2. How we use it">
          <p>We use your information to display your HausCard to other members, power search and discovery, deliver
            messages and intro requests, send you notifications about activity on your account, and manage your
            membership and access.</p>
        </Section>

        <Section title="3. Email notifications">
          <p>When someone requests an intro or messages you, we may send an email to your registered address so you
            don't miss a collaboration. These are transactional emails tied to your account activity.</p>
        </Section>

        <Section title="4. Who can see your work">
          <p>Your published HausCard is visible to paying members inside the Haus. A limited public preview (name,
            avatar, role) may appear on our landing page. Your email address is never shown publicly.</p>
        </Section>

        <Section title="5. Third parties">
          <p>We use trusted service providers to run WriterHaus — for hosting media, sending emails, processing
            payments, and authentication. They only receive the data needed to provide their service and are bound
            to protect it.</p>
        </Section>

        <Section title="6. Your choices">
          <p>You can edit or remove content from your HausCard at any time, and you can delete your account. Contact
            us to request deletion of your personal data.</p>
        </Section>

        <Section title="7. Contact">
          <p>Questions about your privacy? Reach us at info@underground-mix.com.</p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
